/**
 * This module contains the checks to gate features to be enabled only under specific conditions.
 */

import { useFeatureGate } from '@statsig/expo-bindings';

import { User } from './types/User';

/**
 * Statsig gate keys used across the app. Keep one source of truth so a typo
 * in a gate name doesn't silently disable the feature.
 */
export const GATES = {
  ENABLE_LIGHTHOUSE_LOCATIONS: 'enable_lighthouse_locations',
} as const;

/**
 * Returns true iff a user profile represents a Plentiful-internal user.
 */
export function isInternalProfile(profile: Pick<User, 'email_verified' | 'email'>): boolean {
  return (
    !!profile.email_verified &&
    !!profile.email &&
    ((email: string) => {
      const matches = email.match(/\@plentiful.org/);
      return !!matches && matches.length > 0;
    })(profile.email)
  );
}

/**
 * Returns true when Lighthouse (PPR) location data should be fetched and
 * rendered alongside Plentiful's `/map/locations` response. When false, no
 * Lighthouse fetch is issued and no merge runs — the locations facade
 * delegates to Plentiful alone.
 */
export function useEnableLighthouseLocations(): boolean {
  const { value } = useFeatureGate(GATES.ENABLE_LIGHTHOUSE_LOCATIONS);
  return value;
}
